import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Table, Tag, Select } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import * as api from '@apis/chats-v2';
import { useAuthentication } from '@hooks/authentication';
import { ConsultationStatus, GetChatRoom, Participant } from '@models/chat';
import ChatPreviewV2 from '@components/chat/ChatPreviewV2';
import { UserProfile } from '@models/auth';

const CONSULTATION_STATUS_LABEL: Record<ConsultationStatus | 'all', string> = {
  all: '상태 전체',
  [ConsultationStatus.PENDING]: '상담 대기',
  [ConsultationStatus.IN_PROGRESS]: '상담중',
  [ConsultationStatus.COMPLETED]: '상담 완료',
};

const ChatRoomsV2Page: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedChatRoom, setSelectedChatRoom] = useState<GetChatRoom | undefined>();
  const [query, setQuery] = useState<{ page: number; size: number; consultationStatus?: ConsultationStatus | 'all' }>({
    page: 0,
    size: 20,
    consultationStatus: 'all',
  });

  useAuthentication();

  const { data: chatRoomsData, isLoading } = useQuery({
    queryKey: ['chat-rooms-v2', query, searchKeyword],
    queryFn: () => api.getChatRooms({ ...query, keyword: searchKeyword }),
  });

  const columns: ColumnsType<GetChatRoom> = [
    {
      title: '참여자',
      dataIndex: 'participants',
      render: (participants: (Participant & { profile: UserProfile })[]) =>
        (participants || []).map((p) => <div key={p.id}>{p.profile?.name || p.profile?.nickname}</div>),
    },
    {
      title: '마지막 메시지',
      dataIndex: 'lastMessage',
      render: (text) => <div style={{ whiteSpace: 'pre-wrap', maxWidth: 300 }}>{text}</div>,
    },
    {
      title: '상담 상태',
      dataIndex: 'consultationStatus',
      render: (status: ConsultationStatus) => (
        <Tag color={status === 'completed' ? 'green' : status === 'in_progress' ? 'blue' : 'gold'}>
          {CONSULTATION_STATUS_LABEL[status]}
        </Tag>
      ),
    },
    {
      title: '마지막 메시지 시간',
      dataIndex: 'lastMessageAt',
      render: (date) => (date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '채팅 보기',
      key: 'action',
      render: (_, record) => <Button onClick={() => setSelectedChatRoom(record)}>보기</Button>,
    },
  ];

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>CS 채팅 관리</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <FilterSelect
            inactiveValue="all"
            value={query.consultationStatus}
            style={{ width: 150 }}
            onChange={(value) => setQuery((prev) => ({ ...prev, consultationStatus: value, page: 0 }))}>
            {Object.entries(CONSULTATION_STATUS_LABEL).map(([value, label]) => (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            ))}
          </FilterSelect>
          <Input.Search
            className={filterActiveClassName(!!searchKeyword)}
            placeholder="참여자 이름 검색"
            onSearch={(value) => setSearchKeyword(value)}
            style={{ width: 300 }}
          />
        </div>
      </div>
      <div className="page-content">
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={chatRoomsData?.data.contents}
          loading={isLoading}
          pagination={{
            current: query.page + 1,
            pageSize: query.size,
            total: chatRoomsData?.data.total,
            onChange: (page, size) => setQuery({ page: page - 1, size }),
          }}
        />
      </div>
      {selectedChatRoom && (
        <div
          style={{
            width: '428px',
            height: '100vh',
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
            zIndex: 900,
            borderLeft: '1px solid #eaeaea',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <div style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>
            <Button onClick={() => setSelectedChatRoom(undefined)}>닫기</Button>
          </div>
          <ChatPreviewV2 chatRoom={selectedChatRoom} />
        </div>
      )}
    </div>
  );
};

export default ChatRoomsV2Page;
